// src/components/StaffList.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import {
  Typography, Button, Box, Paper, IconButton,
  Chip, Avatar, TextField, InputAdornment, Stack,
  Fade, Divider, Container
} from "@mui/material";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { useNavigate } from 'react-router-dom';
import AppHeader from './AppHeader';

import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import DomainIcon from '@mui/icons-material/Domain';
import BadgeIcon from '@mui/icons-material/Badge';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

ModuleRegistry.registerModules([AllCommunityModule]);

// Custom Cell Renderers
const StatusRenderer = (params) => {
  const active = params.value === 1;
  return (
    <Chip
      label={active ? "Active" : "Inactive"}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 800,
        borderRadius: '4px',
        bgcolor: active ? '#e8f5e9' : '#ffebee',
        color: active ? '#2e7d32' : '#d32f2f',
        border: `1px solid ${active ? '#c8e6c9' : '#ffcdd2'}`
      }}
    />
  );
};

const NameRenderer = (params) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
    <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'primary.main', fontWeight: 700 }}>
      {params.value?.charAt(0)}
    </Avatar>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{params.value}</Typography>
  </Box>
);

const StaffList = ({ setIsLoggedIn }) => {
  const [rowData, setRowData] = useState([]);
  const [quickFilterText, setQuickFilterText] = useState("");
  const gridRef = useRef(null);
  const navigate = useNavigate();

  const columnDefs = useMemo(() => [
    {
      headerName: 'Staff Info',
      children: [
        { headerName: 'Full Name', field: 'full_name', width: 220, cellRenderer: NameRenderer, pinned: 'left' },
        { headerName: 'Code', field: 'staff_code', width: 110, filter: 'agTextColumnFilter' },
      ]
    },
    {
      headerName: 'Account',
      children: [
        { headerName: 'Username', field: 'username', width: 140 },
        { headerName: 'Email', field: 'email', width: 220 },
        { headerName: 'Dept', field: 'department', width: 140 },
        { headerName: 'Role', field: 'role', width: 100, cellRenderer: (p) => <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary' }}>{p.value}</Typography> },
      ]
    },
    {
      headerName: 'Policies',
      children: [
        {
          headerName: 'Status',
          field: 'is_active',
          width: 100,
          cellRenderer: StatusRenderer,
          filter: 'agNumberColumnFilter'
        },
        {
          headerName: 'Work Type',
          field: 'work_type',
          width: 130,
          valueFormatter: (p) => p.value === 'shift_based' ? 'Shift-based' : 'Office'
        },
        {
          headerName: 'OT',
          field: 'ot_eligible',
          width: 90,
          cellRenderer: (p) => p.value === 1 ? <Chip label="YES" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 900, color: '#2e7d32' }} /> : '-'
        },
      ]
    },
    {
      headerName: 'Organization',
      children: [
        { headerName: 'Manager', field: 'manager_name', width: 140 },
        { headerName: 'HR', field: 'hr_name', width: 140 },
        { headerName: 'Locations', field: 'allowed_locations', width: 200 }
      ]
    }
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    suppressHeaderMenuButton: true,
  }), []);

  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_STAFF_API_URL + '/staff', {
          withCredentials: true,
          headers: { "X-Tenant": localStorage.getItem("tenant") }
        });
        const data = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.rows) ? response.data.rows : []);
        setRowData(data);
      } catch (error) { console.error('Error:', error); }
    };
    fetchStaffList();
  }, []);

  const onBtnExport = useCallback(() => gridRef.current.api.exportDataAsCsv(), []);
  const onRowClicked = useCallback(e => navigate('/staff/' + e.data.staff_id), [navigate]);
  const onBtnNewStaff = useCallback(() => navigate('/staff/new'), [navigate]);
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    navigate('/login');
  };

  // Stats
  const activeCount = rowData.filter(r => r.is_active === 1).length;
  const deptCount = new Set(rowData.map(r => r.department)).size;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f4', overflow: 'hidden' }}>
      <AppHeader onLogout={handleLogout} />

      <Container maxWidth="xl" sx={{ flexGrow: 1, py: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
        <Fade in timeout={500}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#1b5e20', letterSpacing: '-1px', mb: 0.5 }}>
                  Staff Directory
                </Typography>
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{activeCount} <Typography component="span" variant="caption" color="text.secondary">Active Members</Typography></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DomainIcon sx={{ color: '#1565c0', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{deptCount} <Typography component="span" variant="caption" color="text.secondary">Departments</Typography></Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  placeholder="Quick Search..."
                  size="small"
                  value={quickFilterText}
                  onChange={(e) => setQuickFilterText(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    sx: { borderRadius: '12px', bgcolor: 'white', width: 250 }
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={onBtnExport}
                  sx={{ borderRadius: '12px', px: 3, fontWeight: 700, borderColor: '#ddd', color: 'text.primary', bgcolor: 'white' }}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onBtnNewStaff}
                  sx={{ borderRadius: '12px', px: 4, fontWeight: 700, bgcolor: '#2e7d32', boxShadow: '0 4px 12px rgba(46,125,50,0.2)' }}
                >
                  New Staff
                </Button>
              </Stack>
            </Box>

            {/* Grid Container */}
            <Paper sx={{
              flexGrow: 1,
              borderRadius: '24px',
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
              mb: 1
            }}>
              <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowHeight={48}
                  headerHeight={40}
                  quickFilterText={quickFilterText}
                  rowSelection={{ mode: "singleRow" }}
                  onRowClicked={onRowClicked}
                  animateRows={true}
                  theme="legacy"
                />
              </div>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default StaffList;